import { logError } from '@core/logging'

/**
 * Represents a message preview object the depends on
 * last read message cursor along with if the user has dismissed the message preview
 * Used in local storage.
 */
export class MessagePreview {
  /**
   * Creates an instance of LastReadMessage.
   * @param lastReadMessageId - The last read message id.
   * @param dismissed - If the user has dismissed the message preview.
   */
  constructor(
    private lastReadMessageId: string,
    private dismissed: boolean,
  ) {}

  /**
   * Get the last read message id from a MessagePreview instance.
   */
  getLastReadMessageId(): string {
    return this.lastReadMessageId
  }

  /**
   * Get if the user has dismissed the message preview from a MessagePreview instance.
   */
  getDismissed(): boolean {
    return this.dismissed
  }

  /**
   * Updates the last read message id and show it.
   */
  updateLastReadMessageId(newReadMessageId: string) {
    this.lastReadMessageId = newReadMessageId
    this.dismissed = false
  }

  /**
   * Dismisses the message preview.
   */
  dismiss() {
    this.dismissed = true
  }

  /**
   * Shows the message preview.
   */
  show() {
    this.dismissed = false
  }

  /**
   * Parses a JSON string and creates an instance of MessagePreview.
   */
  static fromJSON(json: string | undefined): MessagePreview | undefined {
    if (typeof json === 'undefined') {
      return undefined
    }

    try {
      const parsedData = JSON.parse(json)
      if (
        parsedData &&
        parsedData.lastReadMessageId &&
        parsedData.dismissed !== undefined
      ) {
        return new MessagePreview(
          parsedData.lastReadMessageId,
          parsedData.dismissed,
        )
      }
    } catch (error) {
      logError(new Error('Error parsing message preview JSON: ' + error), {
        data: { json },
      })
    }

    return undefined
  }

  /**
   * Returns a JSON string representation of the MessagePreview instance.
   */
  toJSON(): string {
    return JSON.stringify({
      lastReadMessageId: this.lastReadMessageId,
      dismissed: this.dismissed,
    })
  }
}
